import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Typography, Divider } from '@mui/material';

import Button from 'components/Common/Button';
import Modal from 'components/Common/Modal';
import { AppDispatch } from 'redux/store';
import { provisionStaff } from 'redux/slices/staff/staffSlice';
import { selectSelectedParishId, selectEffectiveStaffRole } from 'redux/selectors/parishContext';
import { hasAtLeastRole } from 'api/v1Helpers';
import routes from 'constants/routes';
import CreateStaffForm from 'components/Staff/CreateStaffForm';
import CardWrapper from 'components/Common/CardWrapper';
import type { CreateStaff } from 'types/staff';

export default function CreateNewStaff() {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [successKind, setSuccessKind] = useState<'provisioned' | 'granted'>('provisioned');

    const parishId = useSelector(selectSelectedParishId);
    const actorRole = useSelector(selectEffectiveStaffRole);
    const canInvite = hasAtLeastRole(actorRole, 'MERCHANT_ADMIN');

    const handleCreateStaff = async (formValues: CreateStaff) => {
        if (!parishId || !canInvite) return;
        try {
            setLoading(true);
            const res = await dispatch(
                provisionStaff({
                    parishId,
                    firstName: formValues.firstName,
                    lastName: formValues.lastName,
                    email: formValues.email.trim().toLowerCase(),
                    role: formValues.role,
                })
            );
            setLoading(false);
            if (!(res as { error?: unknown })?.error && provisionStaff.fulfilled.match(res) && res.payload) {
                setSuccessKind(res.payload.kind);
                setModalOpen(true);
            }
        } catch {
            setLoading(false);
        }
    };

    const handleSuccess = () => {
        setModalOpen(false);
        navigate(routes.staff.main);
    };

    if (!parishId) {
        return (
            <CardWrapper>
                <Alert severity="warning">Select a parish before creating staff.</Alert>
            </CardWrapper>
        );
    }

    if (!canInvite) {
        return (
            <CardWrapper>
                <Alert severity="error">You need Merchant Admin or higher to create staff.</Alert>
            </CardWrapper>
        );
    }

    return (
        <CardWrapper>
            <Typography
                sx={{ fontSize: { xs: 16, sm: 24 }, mb: 2, width: { xs: '80%', sm: '50%' }, color: '#220E40', fontWeight: 400 }}
                variant="h1"
            >
                Create Staff
            </Typography>
            <Divider sx={{ borderBottomWidth: 2 }} />
            <Typography sx={{ mt: 2, mb: 4, fontSize: 14 }} variant="body1">
                Enter their name, email, and role. We create their account and queue login instructions by email. New staff sign in with a
                temporary password, then set a permanent password. Existing accounts are granted the role without changing their password.
            </Typography>
            <CreateStaffForm onSubmit={handleCreateStaff} loading={loading} actorRole={actorRole} showRoleSelect />
            <Modal onClose={() => setModalOpen(false)} isOpen={modalOpen}>
                <>
                    <Typography sx={{ fontSize: 18, my: 2 }}>
                        {successKind === 'granted'
                            ? 'Staff role granted. An access notification has been queued for email.'
                            : 'Staff account created. Login instructions have been queued for email.'}
                    </Typography>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', marginTop: 32 }}>
                        <Button onClick={handleSuccess} width={152} title="Done" />
                    </div>
                </>
            </Modal>
        </CardWrapper>
    );
}
